import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import FilterVacantes from "./FilterVacantes.tsx";
import PaginationVacantes from "./PaginationVacantes.tsx";
import EmptyImage from "../../assets/img/ic_content.svg";
import {
  setDefaultSort,
  setPage,
  setSortBy,
} from "../../store/slices/vacantesSlice.ts";
import { fixedHeadStyle } from "../../utils/constanst.js";

export interface UnfilledPositionRow {
  branch: { id: number; name: string; businessName?: { id: number; name: string } };
  position: { id: number; name: string };
  shift: string;
  totalUnfilled: number;
  daysSinceUncovered?: number | null;
}

const TableVacantesV2: React.FC<{ fetchVacantesV2: () => void }> = ({
  fetchVacantesV2,
}) => {
  const dispatch = useDispatch();
  const { vacantes, sortBy, defaultSort } = useSelector(
    (state: RootState) => state.vacantes
  );

  const list = (vacantes || []) as UnfilledPositionRow[];

  const getSortValue = (
    item: UnfilledPositionRow,
    key: string
  ): number | string | null => {
    switch (key) {
      case "branch":
        return item.branch?.name ?? "";
      case "position":
        return item.position?.name ?? "";
      case "shift":
        return item.shift ?? "";
      case "totalUnfilled":
        return item.totalUnfilled != null ? Number(item.totalUnfilled) : null;
      default:
        return (item as any)[key] ?? null;
    }
  };

  const sortedList = list.slice().sort((a, b) => {
    const aVal = getSortValue(a, sortBy);
    const bVal = getSortValue(b, sortBy);
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp =
      typeof aVal === "number" && typeof bVal === "number"
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal), "es");
    return defaultSort === "asc" ? cmp : -cmp;
  });

  const getIconSort = () => (
    <i className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`} />
  );

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const tableContainerStyle: React.CSSProperties = {
    overflow: "auto",
    maxHeight: "500px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  };

  const headerCellStyle: React.CSSProperties = {
    ...(fixedHeadStyle as React.CSSProperties),
    cursor: "pointer",
    padding: "14px 16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
  };

  const rowStyle = (index: number): React.CSSProperties => ({
    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
    transition: "background-color 0.15s ease",
  });

  const cellStyle: React.CSSProperties = {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#1f2937",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  };

  return (
    <React.Fragment>
      <div className="input-group w-100">
        <div className="d-flex align-items-center w-100">
          <FilterVacantes showAddButton={false} />
        </div>
      </div>

      <div className="table-responsive mb-3" style={tableContainerStyle}>
        <table
          className="table table-hover align-middle mb-0 text-nowrap"
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th style={headerCellStyle} onClick={() => setSort("branch")}>
                Sucursal {sortBy === "branch" && getIconSort()}
              </th>
              <th style={headerCellStyle} onClick={() => setSort("position")}>
                Puesto {sortBy === "position" && getIconSort()}
              </th>
              <th style={headerCellStyle} onClick={() => setSort("shift")}>
                Turno {sortBy === "shift" && getIconSort()}
              </th>
              <th
                className="text-center"
                style={headerCellStyle}
                onClick={() => setSort("daysSinceUncovered")}
              >
                Días sin cubrir {sortBy === "daysSinceUncovered" && getIconSort()}
              </th>
              <th
                className="text-center"
                style={headerCellStyle}
                onClick={() => setSort("totalUnfilled")}
              >
                Puestos sin cubrir {sortBy === "totalUnfilled" && getIconSort()}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedList.length > 0 ? (
              sortedList.map((item, index) => (
                <tr
                  key={`${item.branch.id}-${item.position.id}-${item.shift}-${index}`}
                  style={rowStyle(index)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f9ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      index % 2 === 0 ? "#ffffff" : "#fafafa";
                  }}
                >
                  <td style={{ ...cellStyle, fontWeight: 500 }}>
                    {item.branch.name}
                  </td>
                  <td style={cellStyle}>{item.position.name}</td>
                  <td style={cellStyle}>{item.shift}</td>
                  <td className="text-center" style={cellStyle}>
                    {item.daysSinceUncovered != null ? (
                      <span
                        style={{
                          backgroundColor:
                            item.daysSinceUncovered >= 11
                              ? "#fee2e2"
                              : item.daysSinceUncovered >= 8
                              ? "#fef9c3"
                              : "#dcfce7",
                          color:
                            item.daysSinceUncovered >= 11
                              ? "#dc2626"
                              : item.daysSinceUncovered >= 8
                              ? "#854d0e"
                              : "#15803d",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: 600,
                          display: "inline-block",
                          minWidth: "64px",
                        }}
                      >
                        {item.daysSinceUncovered} días
                      </span>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                        —
                      </span>
                    )}
                  </td>
                  <td className="text-center" style={cellStyle}>
                    <span
                      style={{
                        backgroundColor:
                          item.totalUnfilled >= 1 ? "#fee2e2" : "#dcfce7",
                        color: item.totalUnfilled >= 1 ? "#dc2626" : "#15803d",
                        padding: "6px 12px",
                        borderRadius: "999px",
                        fontSize: "13px",
                        fontWeight: 600,
                        display: "inline-block",
                        minWidth: "32px",
                      }}
                    >
                      {item.totalUnfilled}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <div className="py-4">
                    <div className="d-flex">
                      <img src={EmptyImage} alt="" className="mx-auto w-250px" />
                    </div>
                    <h5 className="text-secondary text-center fs-20px">
                      No hay puestos sin cubrir
                    </h5>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationVacantes />
    </React.Fragment>
  );
};

export default TableVacantesV2;
